import { useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@shared/api/supabaseClient';
import { isMobileApp } from '@shared/lib/webview-detection';
import { nativeCallsCapability } from '@shared/lib/native-calls';

const KEYBOARD_OPEN_THRESHOLD_PX = 120;

type NativeBridgeMessage =
  | {
      type: 'supabase_session';
      accessToken: string;
      refreshToken: string;
      expiresAt: number | null;
      userId: string;
      user: Session['user'];
    }
  | {
      type: 'logout';
    };

type MobileHostWindow = Window & {
  flutter_inappwebview?: {
    callHandler?: (name: string, ...args: unknown[]) => Promise<unknown>;
  };
  ReactNativeWebView?: {
    postMessage?: (message: string) => void;
  };
  webkit?: {
    messageHandlers?: Record<
      string,
      {
        postMessage?: (message: NativeBridgeMessage) => void;
      }
    >;
  };
  JemaNativeBridge?: {
    postMessage?: (message: string) => void;
  };
};

const isTextInputElement = (target: EventTarget | null): target is HTMLElement => {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return !['checkbox', 'radio', 'range', 'file', 'button', 'submit', 'reset'].includes(
      target.type
    );
  }
  return target.isContentEditable;
};

const toSessionBridgeMessage = (session: Session): NativeBridgeMessage => ({
  type: 'supabase_session',
  accessToken: session.access_token,
  refreshToken: session.refresh_token,
  expiresAt: session.expires_at ?? null,
  userId: session.user.id,
  user: session.user,
});

const postNativeBridgeMessage = (message: NativeBridgeMessage) => {
  if (typeof window === 'undefined') return;

  const hostWindow = window as MobileHostWindow;
  const serialized = JSON.stringify(message);

  hostWindow.ReactNativeWebView?.postMessage?.(serialized);
  hostWindow.JemaNativeBridge?.postMessage?.(serialized);
  hostWindow.webkit?.messageHandlers?.jemaNativeBridge?.postMessage?.(message);
  const flutter = hostWindow.flutter_inappwebview;
  if (flutter?.callHandler) {
    const request =
      message.type === 'logout'
        ? flutter.callHandler('onLogout')
        : flutter.callHandler('onSessionChanged', serialized);
    void request.catch(() => undefined);
  }
};

/**
 * Global runtime enhancements for mobile webview containers:
 * - keeps a stable viewport-height css variable
 * - marks keyboard-open state on html/body
 * - auto-scrolls focused inputs into view
 * - sends Supabase session changes to the native mobile shell
 */
export function useWebViewNativeExperience(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || !isMobileApp()) return;

    const root = document.documentElement;
    const body = document.body;
    const viewport = window.visualViewport;
    let keyboardOpen = false;

    root.classList.add('webview-app');
    body.classList.add('webview-app');

    const syncViewport = () => {
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      const keyboardInset = Math.max(0, window.innerHeight - viewportHeight - offsetTop);

      root.style.setProperty('--app-vh', `${Math.round(viewportHeight)}px`);
      root.style.setProperty('--keyboard-inset', `${Math.round(keyboardInset)}px`);

      const nextKeyboardOpen = keyboardInset > KEYBOARD_OPEN_THRESHOLD_PX;
      if (nextKeyboardOpen !== keyboardOpen) {
        keyboardOpen = nextKeyboardOpen;
        root.classList.toggle('keyboard-open', keyboardOpen);
        body.classList.toggle('keyboard-open', keyboardOpen);
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!isTextInputElement(event.target)) return;
      const target = event.target;
      window.setTimeout(() => {
        if (!target.isConnected) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }, 140);
      syncViewport();
    };

    const handleFocusOut = () => {
      window.setTimeout(syncViewport, 120);
    };

    syncViewport();

    window.addEventListener('resize', syncViewport);
    viewport?.addEventListener('resize', syncViewport);
    viewport?.addEventListener('scroll', syncViewport);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      window.removeEventListener('resize', syncViewport);
      viewport?.removeEventListener('resize', syncViewport);
      viewport?.removeEventListener('scroll', syncViewport);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);

      root.classList.remove('webview-app', 'keyboard-open');
      body.classList.remove('webview-app', 'keyboard-open');
      root.style.removeProperty('--app-vh');
      root.style.removeProperty('--keyboard-inset');
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !isMobileApp()) return;

    let disposed = false;

    const acceptNativeSession = (event: Event) => {
      if (!nativeCallsCapability()) return;
      const detail: unknown = (event as CustomEvent<unknown>).detail;
      if (!detail || typeof detail !== 'object') return;
      const session = detail as {
        access_token?: unknown;
        refresh_token?: unknown;
        expires_at?: unknown;
      };
      if (
        typeof session.access_token !== 'string' ||
        !session.access_token ||
        typeof session.refresh_token !== 'string' ||
        !session.refresh_token ||
        typeof session.expires_at !== 'number' ||
        session.expires_at <= Date.now() / 1000
      )
        return;
      // Native serializes refresh. Only accept a current access token here, so setSession
      // cannot trigger a second refresh with the rotating token.
      void supabase.auth
        .setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        })
        .catch(() => undefined);
    };
    window.addEventListener('jema-native-session', acceptNativeSession);
    if (nativeCallsCapability()) {
      void (window as MobileHostWindow).flutter_inappwebview
        ?.callHandler?.('jemaCallsV2', { version: 1, type: 'sessionListenerReady' })
        .then(session => {
          if (!disposed && session) {
            acceptNativeSession(new CustomEvent('jema-native-session', { detail: session }));
          }
        })
        .catch(() => undefined);
    }

    const publishCurrentSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (disposed) return;

      if (session) postNativeBridgeMessage(toSessionBridgeMessage(session));
      else if (!nativeCallsCapability()) postNativeBridgeMessage({ type: 'logout' });
    };

    void publishCurrentSession();

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        postNativeBridgeMessage({ type: 'logout' });
        return;
      }
      if (!session) {
        if (!nativeCallsCapability()) postNativeBridgeMessage({ type: 'logout' });
        return;
      }

      if (
        event === 'SIGNED_IN' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'USER_UPDATED' ||
        event === 'INITIAL_SESSION'
      ) {
        postNativeBridgeMessage(toSessionBridgeMessage(session));
      }
    });

    return () => {
      disposed = true;
      window.removeEventListener('jema-native-session', acceptNativeSession);
      data.subscription.unsubscribe();
    };
  }, []);
}
